<script setup>
    import { computed, defineAsyncComponent, ref } from 'vue'
    import { useChannelsStore } from '@/stores/channels'
    import ColorInput from 'vue-color-input'

    import InputFormHorizontal from '@/components/UI/Form/InputFormHorizontal.vue'
    import InputCheckBoxWithDescription from '@/components/UI/Form/InputCheckBoxWithDescription.vue'
    import DivDivider from '@/components/utils/DivDivider.vue'
    import ToolbarFile from '@/components/library/ToolbarFile.vue'
    import ButtonForm from '@/components/utils/ButtonForm.vue'
    import AlertPrice from '@/views/dash/channels/Pages/AlertPrice.vue'
    import FeedbackAlert from '@/components/utils/FeedbackAlert.vue'
    import WebchatAvatarCard from '@/views/dash/channels/Pages/WebchatAvatarCard.vue'

    const TextAreaFormHorizontal = defineAsyncComponent(() => import('@/components/UI/Form/TextAreaFormHorizontal.vue'))

    const store = useChannelsStore()
    const channel = store.channel

    const defaultWidgetSize = {
        closed: { width: 76, height: 76 },
        open: { width: 344, height: 575 },
    }

    const widgetSizeLimits = {
        closed: { min: 56, max: 120 },
        open: { widthMin: 280, widthMax: 500, heightMin: 400, heightMax: 800 },
    }

    const widgetSizeRules = {
        closedWidth: `required|integer|min_value:${widgetSizeLimits.closed.min}|max_value:${widgetSizeLimits.closed.max}`,
        closedHeight: `required|integer|min_value:${widgetSizeLimits.closed.min}|max_value:${widgetSizeLimits.closed.max}`,
        openWidth: `required|integer|min_value:${widgetSizeLimits.open.widthMin}|max_value:${widgetSizeLimits.open.widthMax}`,
        openHeight: `required|integer|min_value:${widgetSizeLimits.open.heightMin}|max_value:${widgetSizeLimits.open.heightMax}`,
    }

    store.channel.more = {
        ...channel.more,
        bgColor: channel.more?.bgColor || '#000',
        title: channel.more?.title || '',
        description: channel.more?.description || '',
        greeting: channel.more?.greeting || '',
        userAvatar: channel.more?.userAvatar || '',
        widgetAvatar: channel.more?.widgetAvatar || '',
        showSendButton: channel.more?.showSendButton === true,
        widgetSize: {
            closed: {
                width: channel.more?.widgetSize?.closed?.width ?? defaultWidgetSize.closed.width,
                height: channel.more?.widgetSize?.closed?.height ?? defaultWidgetSize.closed.height,
            },
            open: {
                width: channel.more?.widgetSize?.open?.width ?? defaultWidgetSize.open.width,
                height: channel.more?.widgetSize?.open?.height ?? defaultWidgetSize.open.height,
            },
        },
    }
    store.channel.type = store.channel.type || 'webchat'
    store.channel.server_type = store.channel.server_type || 'webchat'
    store.channel.user = store.channel.user || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

    function handleAvatarSelected(item) {
        store.channel.more.userAvatar = item?.source ?? ''
    }

    function handleWidgetAvatarSelected(item) {
        store.channel.more.widgetAvatar = item?.source ?? ''
    }

    function handleShowSendButtonChange(checked) {
        store.channel.more.showSendButton = !!checked
    }

    const widgetSizeResetKey = ref(0)

    function restoreDefaultWidgetSize() {
        store.channel.more.widgetSize = {
            closed: { ...defaultWidgetSize.closed },
            open: { ...defaultWidgetSize.open },
        }
        widgetSizeResetKey.value++
    }

    const script = computed(() => {
        if (!store.channel?.api_token) return ''
        return `<script onload="init('${store.channel.api_token}')" defer async src="${window.origin}/_widgets/init.js">`
    })

    const urlExternal = computed(() => {
        if (!store.channel?.api_token) return ''
        let url = window.origin
        // caso a url possua o localhost, substitui por vmix.chatmix
        if(url.includes('localhost')) {
            url = 'https://vmix.chatmix.com.br'
        }
        //retorno o url
        return `${url}/_widgets/web-chat-full?api_key=${store.channel.api_token}`
    })
</script>

<template>
    <template v-if="store.formStep === 2">
        <div class="space-y-4">
            <template v-if="script.length">
                <InputFormHorizontal
                    :init-value="`${script}</script>`"
                    label="Script do Webchat"
                    vertical
                    read-only
                    copy-button
                />

                <FeedbackAlert
                    title="Integrar o Webchat é bem simples!"
                    content="Basta copiar o código script acima e colar em seu site, no(s) arquivo(s) onde deseja que utilizá-lo. <b>Em caso de quaisquer dúvida sobre o procedimento, consulte o desenvolvedor do seu site!</b> "
                    type="info"
                />
            </template>
            <InputFormHorizontal
                    :init-value="`${urlExternal}`"
                    label="Url do Webchat"
                    hint="utilize essa url para adicionar no appdoprovedor"
                    vertical
                    read-only
                    copy-button
                />
            <InputFormHorizontal
                v-model="store.channel.name"
                :init-value="store.channel.name"
                label="Nome do webchat"
                placeholder="Como identificará seu canal"
                rules="required"
                vertical
            />

            <InputFormHorizontal
                v-model="store.channel.more.title"
                :init-value="store.channel.more.title"
                label="Título do webchat"
                placeholder="Será exibido no topo do webchat"
                rules="required"
                vertical
            />

            <InputFormHorizontal
                v-model="store.channel.more.description"
                :init-value="store.channel.more.description"
                label="Subtítulo do webchat"
                placeholder="Breve frase, logo abaixo do título"
                rules="required"
                vertical
            />
            <TextAreaFormHorizontal
                v-model="store.channel.more.greeting"
                :name="`message-de-saudacao`"
                label="Mensagem de Saudação"
                placeholder="Escreve a mensagem que será enviada ao usuário quando ele iniciar o webchat"
                hint="Deixe vazio para não enviar a mensagem"
                class="mb-4 w-full"
            />

            <InputCheckBoxWithDescription
                :model-value="!!store.channel.more.showSendButton"
                label="Exibir botão para enviar mensagem"
                description="Por padrão o botão não é exibido. Marque para mostrar."
                @update:model-value="handleShowSendButtonChange"
            />

            <DivDivider class="my-5" />

            <div class="space-y-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div class="space-y-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-gray-400">
                            Tamanho do widget
                        </p>
                        <p class="text-sm text-gray-400 dark:text-gray-400">
                            Valores em pixels. Fechado: botão flutuante. Aberto: janela do chat.
                        </p>
                    </div>
                    <ButtonForm
                        type="button"
                        color="gray"
                        size="sm"
                        content="Restaurar padrão"
                        icon="redo"
                        title-button="Restaurar tamanho padrão do sistema (76×76 fechado, 344×575 aberto)"
                        @click="restoreDefaultWidgetSize"
                    />
                </div>

                <div
                    :key="widgetSizeResetKey"
                    class="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                    <InputFormHorizontal
                        v-model="store.channel.more.widgetSize.closed.width"
                        :init-value="store.channel.more.widgetSize.closed.width"
                        name="webchat_largura_fechado"
                        label="Largura fechado (px)"
                        type="number"
                        :min="String(widgetSizeLimits.closed.min)"
                        :max="String(widgetSizeLimits.closed.max)"
                        :rules="widgetSizeRules.closedWidth"
                        :hint="`Informe um valor entre ${widgetSizeLimits.closed.min} e ${widgetSizeLimits.closed.max} pixels.`"
                        vertical
                    />
                    <InputFormHorizontal
                        v-model="store.channel.more.widgetSize.closed.height"
                        :init-value="store.channel.more.widgetSize.closed.height"
                        name="webchat_altura_fechado"
                        label="Altura fechado (px)"
                        type="number"
                        :min="String(widgetSizeLimits.closed.min)"
                        :max="String(widgetSizeLimits.closed.max)"
                        :rules="widgetSizeRules.closedHeight"
                        :hint="`Informe um valor entre ${widgetSizeLimits.closed.min} e ${widgetSizeLimits.closed.max} pixels.`"
                        vertical
                    />
                    <InputFormHorizontal
                        v-model="store.channel.more.widgetSize.open.width"
                        :init-value="store.channel.more.widgetSize.open.width"
                        name="webchat_largura_aberto"
                        label="Largura aberto (px)"
                        type="number"
                        :min="String(widgetSizeLimits.open.widthMin)"
                        :max="String(widgetSizeLimits.open.widthMax)"
                        :rules="widgetSizeRules.openWidth"
                        :hint="`Informe um valor entre ${widgetSizeLimits.open.widthMin} e ${widgetSizeLimits.open.widthMax} pixels.`"
                        vertical
                    />
                    <InputFormHorizontal
                        v-model="store.channel.more.widgetSize.open.height"
                        :init-value="store.channel.more.widgetSize.open.height"
                        name="webchat_altura_aberto"
                        label="Altura aberto (px)"
                        type="number"
                        :min="String(widgetSizeLimits.open.heightMin)"
                        :max="String(widgetSizeLimits.open.heightMax)"
                        :rules="widgetSizeRules.openHeight"
                        :hint="`Informe um valor entre ${widgetSizeLimits.open.heightMin} e ${widgetSizeLimits.open.heightMax} pixels.`"
                        vertical
                    />
                </div>
            </div>

            <DivDivider class="my-5" />

            <div class="flex flex-col gap-6 w-full input">
                <div class="w-full space-y-2">
                    <label
                        for="color"
                        class="block text-sm font-medium text-gray-900 dark:text-gray-400"
                    >
                        Cor do Webchat
                    </label>
                    <ColorInput
                        id="color"
                        v-model="store.channel.more.bgColor"
                        position="top right"
                        format="hex string"
                    />
                </div>

                <div class="space-y-3">
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-400">
                        Avatares do webchat
                    </p>
                    <p class="text-sm text-gray-400 dark:text-gray-400">
                        Cada imagem aparece em um local diferente do widget. Tamanho recomendado: 200×200 px.
                    </p>

                    <div class="flex flex-col gap-4">
                        <WebchatAvatarCard
                            badge="Chat aberto"
                            title="Avatar do agente"
                            hint="Caso não seja selecionado, é usado o avatar padrão do agente."
                            toolbar-title="Clique para selecionar ou enviar o avatar do agente"
                            variant="agent"
                            :source="store.channel.more.userAvatar"
                            :avatar="store.channel.more.userAvatar"
                            :bg-color="store.channel.more.bgColor"
                            :preview-title="store.channel.more.title || 'Atendimento'"
                            @selected="handleAvatarSelected"
                        />

                        <WebchatAvatarCard
                            badge="Balão fechado"
                            title="Icone do botão fechado"
                            hint="Caso não seja selecionado, é exibido o ícone padrão de chat."
                            toolbar-title="Clique para selecionar ou enviar o avatar do botão"
                            variant="bubble"
                            :source="store.channel.more.widgetAvatar"
                            :avatar="store.channel.more.widgetAvatar"
                            :bg-color="store.channel.more.bgColor"
                            :bubble-size="store.channel.more.widgetSize?.closed?.width ?? 76"
                            @selected="handleWidgetAvatarSelected"
                        />
                    </div>
                </div>
            </div>

            <AlertPrice />
        </div>
    </template>

    <template v-else-if="store.formStep === 1">
        Instruções de cadastro
    </template>
</template>

<style scoped>
    :deep(.color-input .picker-popup) {
        z-index: 120000 !important;
    }
</style>
